import {
  type AvailabilityView,
  type CardScheme,
  type ColumnGrouping,
  type ColumnShading,
  type ColumnWidth,
  DEFAULT_SETTINGS,
  type FormLayout,
  type PreviewMode,
  type RoomLanesPreference,
  type SchedulerSettings,
  type SchedulerView,
  type StaffPreset
} from "./settings";
import type { AppointmentTypeId, ClinicId } from "./types";

/**
 * The wire shape of the per-user settings store, and the only place that knows how a
 * `SchedulerSettings` value survives a round trip through it (ENG-2047).
 *
 * Pure: no React, no RTK Query, no storage. The hook that owns the traffic
 * (`state/use-settings-sync.hook.ts`) reads with `readSchedulerDocument` and writes with
 * `writeSchedulerDocument`, so every rule about versions, scopes and unknown keys is
 * testable without a store.
 */

/** A row per (user, practice): the scheduler's own layout. */
export const SCHEDULER_NAMESPACE = "scheduler";
/** A row per user, practice NULL: ENG-1981's "use the new scheduler" opt-in. */
export const OPT_IN_NAMESPACE = "scheduling_v3";

/**
 * Bumped ONLY by an incompatible reshape — a key moving bag, or its meaning changing.
 * Adding or removing a key never bumps it, because the reader drops what it does not know
 * and defaults what is missing. The reader ships one release before the writer, so a tab
 * on the older build reads the newer document; it never writes it (`versionHold`).
 */
export const SETTINGS_DOCUMENT_VERSION = 1;

/** The server refuses a larger document; the cache refuses to store one. */
export const SETTINGS_DOCUMENT_MAX_BYTES = 65_536;

/**
 * How the scoped keys are keyed. Decided on every load by `User.scheduler_persist_per_clinic`
 * (ENG-2194, Chen's option A, as classic does); the document's `scope_mode` is only a record
 * of how it was last written.
 */
export type ScopeMode = "all" | "clinic";

/** Everything except `slotMinutes`, which is practice-derived (`Practice.scheduling_cell_duration`). */
export type PersistedSettingKey = Exclude<
  keyof SchedulerSettings,
  "slotMinutes"
>;

export interface SchedulerDocument {
  v: number;
  scope_mode: ScopeMode;
  /** The 14 keys that are the same wherever the user is. */
  shared: Record<string, unknown>;
  /** The 5 clinic-specific keys, per scope key (`all`, `region:<id>`, `clinic:<id>`). */
  scopes: Record<string, Record<string, unknown>>;
  /** A future key written by a newer build round-trips untouched. */
  [unknown: string]: unknown;
}

type Validator<K extends PersistedSettingKey> = (
  raw: unknown
) => SchedulerSettings[K] | undefined;

const isPlainObject = (raw: unknown): raw is Record<string, unknown> =>
  typeof raw === "object" && raw !== null && !Array.isArray(raw);

const oneOf =
  <T extends string>(allowed: readonly T[]) =>
  (raw: unknown): T | undefined =>
    typeof raw === "string" && (allowed as readonly string[]).includes(raw)
      ? (raw as T)
      : undefined;

const boolean = (raw: unknown): boolean | undefined =>
  typeof raw === "boolean" ? raw : undefined;

const idList = (raw: unknown): number[] | undefined =>
  Array.isArray(raw) && raw.every((id) => Number.isInteger(id) && id > 0)
    ? (raw as number[])
    : undefined;

/** Minutes from midnight. 1440 is a legitimate end of day. */
const minuteOfDay = (raw: unknown): number | undefined =>
  Number.isInteger(raw) && (raw as number) >= 0 && (raw as number) <= 1440
    ? (raw as number)
    : undefined;

/**
 * One validator per persisted key. A key with no entry here is not persisted and not
 * read — which is why `settings-document.test.ts` asserts this map covers every
 * `DEFAULT_SETTINGS` key but `slotMinutes`. That test is the only guard: there is no tsc
 * gate on this repo and Jest does not run in CI.
 */
export const SETTINGS_VALIDATORS: {
  [K in PersistedSettingKey]: Validator<K>;
} = {
  view: oneOf<SchedulerView>(["day", "week", "workWeek", "month"]),
  grouping: oneOf<ColumnGrouping>(["provider", "clinic"]),
  clinicIds: idList as Validator<"clinicIds">,
  providerIds: idList as Validator<"providerIds">,
  staffListIds: idList as Validator<"staffListIds">,
  columnOrder: idList as Validator<"columnOrder">,
  // `null` is a value here: the list was hand-edited, so no preset is highlighted.
  staffPreset: (raw) =>
    raw === null ? null : oneOf<StaffPreset>(["me", "team", "everyone"])(raw),
  appointmentTypeIds: (raw) =>
    // `null` is a value, not an absence: it means every type, including ones added later.
    raw === null ? null : (idList(raw) as AppointmentTypeId[] | undefined),
  showBlocks: boolean,
  showPersonalEvents: boolean,
  showCancelled: boolean,
  showWebDrafts: boolean,
  showAllStaff: boolean,
  showInactiveStaff: boolean,
  dayStartMinutes: minuteOfDay,
  dayEndMinutes: minuteOfDay,
  formLayout: oneOf<FormLayout>(["classic", "desk"]),
  previewMode: oneOf<PreviewMode>(["docked", "floating"]),
  sidePanelCollapsed: boolean,
  includeSupervising: boolean,
  // ENG-2103 added this after this file was written, and the coverage test below caught it.
  // Shared, not scoped: it is a display toggle like the other `show*` keys, not a selection.
  // Note it DOES persist, where the old side sheet's checkbox was session-only and started
  // unticked every time (ENG-2101) — the same difference every other toggle here already has.
  // No longer read since ENG-2292 (the side panel lists service locations itself); kept to round-trip.
  showServiceLocations: boolean,
  // ENG-2145, toggle 2. Shared: the experience is the same wherever the user is.
  newBookingExperience: boolean,
  // ENG-2055. Shared: how a card looks is the same wherever the user is.
  cardScheme: oneOf<CardScheme>(["accent", "filled"]),
  // SN-20010. Shared, like cardScheme: how the grid looks is the same wherever the user is.
  columnShading: oneOf<ColumnShading>(["provider", "plain"]),
  // SN-20011. Shared: a person's grid looks the same wherever they are.
  columnWidth: oneOf<ColumnWidth>(["comfortable", "fit"]),
  // ENG-2194. Shared: a person's form opens the same way wherever they are.
  roomLanes: oneOf<RoomLanesPreference>(["open", "closed", "remember"]),
  roomLanesLastOpen: boolean,
  // SN-20014. Shared, like roomLanes: a person's form looks the same wherever they are.
  availabilityView: oneOf<AvailabilityView>(["nextAvailable", "staff"]),
  // ENG-2104's Radar rail, shared like `sidePanelCollapsed`: whether the front desk keeps
  // the day's exceptions beside the grid is a layout preference, not a clinic selection.
  radarOpen: boolean
};

/** Clinic-specific selections: they change when the user moves between clinics. */
export const SCOPED_KEYS = [
  "clinicIds",
  "providerIds",
  "staffListIds",
  // Per clinic, as classic keys its order by the scheduler storage key (ENG-2166).
  "columnOrder",
  "staffPreset",
  "appointmentTypeIds"
] as const satisfies readonly PersistedSettingKey[];

/** Everything else: the same wherever the user is. */
export const SHARED_KEYS = [
  "view",
  "grouping",
  "showBlocks",
  "showPersonalEvents",
  "showCancelled",
  "showWebDrafts",
  "showAllStaff",
  "showInactiveStaff",
  "dayStartMinutes",
  "dayEndMinutes",
  "formLayout",
  "previewMode",
  "sidePanelCollapsed",
  "includeSupervising",
  "showServiceLocations",
  "newBookingExperience",
  "cardScheme",
  "columnShading",
  "columnWidth",
  "roomLanes",
  "roomLanesLastOpen",
  "availabilityView",
  "radarOpen"
] as const satisfies readonly PersistedSettingKey[];

const SCOPED = new Set<string>(SCOPED_KEYS);

const isPersistedKey = (key: string): key is PersistedSettingKey =>
  Object.prototype.hasOwnProperty.call(SETTINGS_VALIDATORS, key);

export interface SchedulerScopeKeyInput {
  scopeMode: ScopeMode;
  /** The header clinic — `selectCurrentClinic`, which is what the old scheduler keys on. */
  currentClinicId?: ClinicId | null;
  regionId?: number | null;
  hasRegions?: boolean;
}

/**
 * Which bag of the 5 scoped keys applies right now. The practice is NOT in here — it is
 * the row, so a scope key can never leak one practice's selections into another.
 */
export function schedulerScopeKey({
  scopeMode,
  currentClinicId,
  regionId,
  hasRegions
}: SchedulerScopeKeyInput): string {
  if (scopeMode === "clinic" && currentClinicId != null) {
    return `clinic:${currentClinicId}`;
  }
  if (hasRegions && regionId != null) return `region:${regionId}`;
  return "all";
}

export interface ReadSchedulerDocumentResult {
  /** Only the keys the document actually carried, validated. Absent keys are absent. */
  values: Partial<SchedulerSettings>;
  /** The user's mode, as passed in. The document never overrides it (ENG-2194). */
  scopeMode: ScopeMode;
  /** The document's own `scope_mode` record, or `null` when it has none readable. */
  storedScopeMode: ScopeMode | null;
  storedVersion: number;
  /** The stored `v` is ahead of this build: read what we know, write nothing. */
  versionHold: boolean;
}

const EMPTY_READ: ReadSchedulerDocumentResult = {
  values: {},
  scopeMode: "all",
  storedScopeMode: null,
  storedVersion: SETTINGS_DOCUMENT_VERSION,
  versionHold: false
};

function readBag(
  bag: unknown,
  wantScoped: boolean,
  into: Partial<SchedulerSettings>
): void {
  if (!isPlainObject(bag)) return;
  for (const [key, raw] of Object.entries(bag)) {
    // An unknown key, or a key stored in the wrong bag, is dropped rather than trusted.
    if (!isPersistedKey(key) || SCOPED.has(key) !== wantScoped) continue;
    const value = (SETTINGS_VALIDATORS[key] as Validator<PersistedSettingKey>)(
      raw
    );
    // `undefined` means "invalid" — the caller then falls back to DEFAULT_SETTINGS.
    if (value !== undefined) {
      into[key] = value as SchedulerSettings[typeof key];
    }
  }
}

/**
 * Never throws, never returns a value the grid cannot draw. Garbage in means
 * `DEFAULT_SETTINGS`, which is exactly what a first-time user gets.
 *
 * `userScopeMode` is how this user wants the scoped keys keyed, read by the caller from
 * `User.scheduler_persist_per_clinic` (Personal Details) on every load. It is the answer,
 * whatever the document's `scope_mode` says: classic follows that field on every load, and
 * until ENG-2194 V3 only seeded from it, so changing it after a first visit did nothing.
 * Switching modes loses nothing — every bag stays in `scopes`, and this reads only the one
 * `scopeKey` names.
 */
export function readSchedulerDocument(
  raw: unknown,
  scopeKey: string,
  userScopeMode: ScopeMode = "all"
): ReadSchedulerDocumentResult {
  if (!isPlainObject(raw)) return { ...EMPTY_READ, scopeMode: userScopeMode };

  const storedVersion = Number.isInteger(raw.v)
    ? (raw.v as number)
    : SETTINGS_DOCUMENT_VERSION;
  const values: Partial<SchedulerSettings> = {};
  readBag(raw.shared, false, values);
  const scopes = isPlainObject(raw.scopes) ? raw.scopes : undefined;
  readBag(scopes?.[scopeKey], true, values);

  // The one cross-field rule: an inverted day window would draw a grid with no rows, and
  // each bound is individually in range, so no per-field validator can catch it.
  if (
    values.dayStartMinutes !== undefined &&
    values.dayEndMinutes !== undefined &&
    values.dayEndMinutes <= values.dayStartMinutes
  ) {
    delete values.dayStartMinutes;
    delete values.dayEndMinutes;
  }

  return {
    values,
    scopeMode: userScopeMode,
    storedScopeMode:
      oneOf<ScopeMode>(["all", "clinic"])(raw.scope_mode) ?? null,
    storedVersion,
    versionHold: storedVersion > SETTINGS_DOCUMENT_VERSION
  };
}

export interface WriteSchedulerDocumentInput {
  /** The raw namespace document as the server last returned it. */
  base: unknown;
  /** Current in-session settings. Only `touched` keys are read out of it. */
  values: SchedulerSettings;
  /** Keys changed this session and not yet acknowledged. */
  touched: Iterable<PersistedSettingKey>;
  scopeKey: string;
  scopeMode: ScopeMode;
}

/**
 * The base with the touched keys overlaid, and nothing else changed. Every other key,
 * every other scope and every key this build does not know survive byte for byte, so a
 * stale tab can only ever revert what it itself changed.
 */
export function writeSchedulerDocument({
  base,
  values,
  touched,
  scopeKey,
  scopeMode
}: WriteSchedulerDocumentInput): SchedulerDocument {
  const raw = isPlainObject(base) ? base : {};
  const { v, shared, scopes, scope_mode: _dropped, ...rest } = raw;

  const nextShared: Record<string, unknown> = isPlainObject(shared)
    ? { ...shared }
    : {};
  const nextScopes: Record<string, Record<string, unknown>> = {};
  if (isPlainObject(scopes)) {
    for (const [key, bag] of Object.entries(scopes)) {
      if (isPlainObject(bag)) nextScopes[key] = { ...bag };
    }
  }
  const nextScope: Record<string, unknown> = {
    ...(nextScopes[scopeKey] ?? {})
  };

  for (const key of touched) {
    // A caller cannot persist something that has no validator — `slotMinutes` above all.
    if (!isPersistedKey(key)) continue;
    if (SCOPED.has(key)) nextScope[key] = values[key];
    else nextShared[key] = values[key];
  }
  if (Object.keys(nextScope).length > 0) nextScopes[scopeKey] = nextScope;

  return {
    ...rest,
    // Never downgrade: a document we could not write is never reached (see `versionHold`).
    v: Number.isInteger(v) ? (v as number) : SETTINGS_DOCUMENT_VERSION,
    scope_mode: scopeMode,
    shared: nextShared,
    scopes: nextScopes
  };
}

/** How many bytes the server will weigh, so the 64 KB refusal is never a surprise. */
export function documentByteSize(document: unknown): number {
  return JSON.stringify(document)?.length ?? 0;
}

/** ENG-1981's opt-in. Anything but a real `true` is "not opted in". */
export function readOptInDocument(raw: unknown): boolean {
  return isPlainObject(raw) && raw.opt_in === true;
}

export function writeOptInDocument(
  base: unknown,
  optIn: boolean,
  at: string
): Record<string, unknown> {
  const raw = isPlainObject(base) ? base : {};
  return {
    ...raw,
    v: SETTINGS_DOCUMENT_VERSION,
    opt_in: optIn,
    opt_in_changed_at: at
  };
}

/** Why someone went back to the classic scheduler — the question's six answers (ENG-1981). */
export const OPT_OUT_REASONS = [
  "too_slow",
  "missing_feature",
  "hard_to_read",
  "couldnt_find",
  "prefer_classic",
  "other"
] as const;
export type OptOutReasonKey = (typeof OPT_OUT_REASONS)[number];

export interface OptOut {
  at: string;
  /** The row follows the person across practices, so each answer names where it was given. */
  practiceId: number;
  /** `null` is a skipped question. */
  reason: OptOutReasonKey | null;
  detail: string | null;
  /**
   * Which control asked the question, e.g. "side_panel" (ENG-2385). The backend records it
   * with the answer as feature feedback. Absent on entries saved before ENG-2385.
   */
  source?: string;
}

export interface OptInState {
  optIn: boolean;
  /** How often the offer banner was dismissed. Two is final: it never shows again. */
  notNow: { count: number; lastAt: string | null };
  /** Oldest first. */
  optOuts: OptOut[];
  /** When the new scheduler's guide callout was opened or dismissed (ENG-2384). */
  guideSeenAt: string | null;
}

// Bounds that keep the row far below SETTINGS_DOCUMENT_MAX_BYTES however often someone leaves.
const MAX_OPT_OUTS = 10;
const MAX_DETAIL_LENGTH = 500;
const MAX_NOT_NOW = 2;

const readReason = (raw: unknown): OptOutReasonKey | null =>
  OPT_OUT_REASONS.includes(raw as OptOutReasonKey)
    ? (raw as OptOutReasonKey)
    : null;

const readSource = (raw: unknown): string | undefined =>
  typeof raw === "string" && /^[a-z0-9_]{1,64}$/.test(raw) ? raw : undefined;

const readDetail = (raw: unknown): string | null =>
  typeof raw === "string" && raw.trim()
    ? raw.trim().slice(0, MAX_DETAIL_LENGTH)
    : null;

/** The whole opt-in document, read tolerantly: anything malformed reads as empty. */
export function readOptInState(raw: unknown): OptInState {
  const doc = isPlainObject(raw) ? raw : {};
  const notNow = isPlainObject(doc.not_now) ? doc.not_now : {};
  const count = Number.isInteger(notNow.count)
    ? Math.min(Math.max(notNow.count as number, 0), MAX_NOT_NOW)
    : 0;
  const optOuts = Array.isArray(doc.opt_outs)
    ? doc.opt_outs.filter(isPlainObject).map((entry) => ({
        at: String(entry.at ?? ""),
        practiceId: Number(entry.practice_id),
        reason: readReason(entry.reason),
        detail: readDetail(entry.detail),
        source: readSource(entry.source)
      }))
    : [];
  return {
    optIn: readOptInDocument(doc),
    notNow: {
      count,
      lastAt:
        count > 0 && typeof notNow.last_at === "string" ? notNow.last_at : null
    },
    optOuts,
    guideSeenAt:
      typeof doc.guide_seen_at === "string" && doc.guide_seen_at
        ? doc.guide_seen_at
        : null
  };
}

/** The guide callout was opened or dismissed; it then shrinks to a link for good (ENG-2384). */
export function writeGuideSeen(
  base: unknown,
  at: string
): Record<string, unknown> {
  const raw = isPlainObject(base) ? base : {};
  return { ...raw, v: SETTINGS_DOCUMENT_VERSION, guide_seen_at: at };
}

/** The offer banner was dismissed once more (stops counting at two). */
export function writeNotNow(
  base: unknown,
  at: string
): Record<string, unknown> {
  const raw = isPlainObject(base) ? base : {};
  const { count } = readOptInState(raw).notNow;
  return {
    ...raw,
    v: SETTINGS_DOCUMENT_VERSION,
    not_now: { count: Math.min(count + 1, MAX_NOT_NOW), last_at: at }
  };
}

/** Back to the classic scheduler, with the answer to "why?" (newest ten kept). */
export function writeOptOut(
  base: unknown,
  optOut: OptOut
): Record<string, unknown> {
  const raw = isPlainObject(base) ? base : {};
  const entries = [...readOptInState(raw).optOuts, optOut]
    .slice(-MAX_OPT_OUTS)
    .map((entry) => ({
      at: entry.at,
      practice_id: entry.practiceId,
      reason: readReason(entry.reason),
      detail: readDetail(entry.detail),
      source: readSource(entry.source)
    }));
  return {
    ...writeOptInDocument(raw, false, optOut.at),
    opt_outs: entries
  };
}

/** Handy for a reader that wants every default plus whatever the document carried. */
export function withDefaults(
  values: Partial<SchedulerSettings>
): SchedulerSettings {
  return { ...DEFAULT_SETTINGS, ...values };
}
